import { isPublicNext, publicPreviewCode } from "../../next/public/mode";
import { rememberWantedPage } from "../../utils/wantedPage";
import { generalActions } from "./../store/reducers/generalReducer";
import {
  CreateRoute,
  // GetNotification,
} from "./../../types/api/index";
import axios, { AxiosInstance } from "axios";
import {
  // GetHouseholds,
  GetRoute,
  GetRoutes,
  UpdateRoute,
  GetTeam,
  GetNotifications,
} from "../../types/api";
// import { Household } from "../../types/global/types";
import { LoginUser, RegUser, LoginWithToken } from "../../types/types";
import { store } from "../store";
import { API_URL } from "../../constants/base";
import { lockedLandingPath } from "../../next/routes/lockedLanding";
import { OfflineWriteBlockedError, shouldBlockWrite } from "../../offline/writeGuard";
import i18n from "../../res/languages";
import { isRealToken } from "../../utils/updateToken";
import { CONTACT_LIMIT_EVENT, contactLimitFrom, OVER_LIMIT_EVENT, overLimitFrom } from "../../utils/contactLimit";
import { noteBillingRefusal } from "../../utils/billingRefusals";
import { ONBOARDING_ROOT } from "../sagas/onboardingRedirect";
import { DeletedLogin, UserDeletionDecision, UserDeletionImpact, UserRestoreImpact } from "../../types/adminDeletion";

const instance: AxiosInstance = axios.create({
  baseURL: API_URL,
});

/** The browser's IANA time zone ("America/Chicago"), or null where the browser cannot say. */
export const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

instance.interceptors.request.use((request: any) => {
  // Writes with no offline story are stopped here rather than being fired into a
  // dead network. Tier 1 writes never reach this point while offline — their sagas
  // enqueue instead of calling the API — so anything arriving here offline is
  // something we genuinely cannot honour until the connection returns.
  if (shouldBlockWrite(request.method)) {
    store.dispatch(
      generalActions.errorAC({
        message: i18n.t("offline.write_needs_connection"),
        type: "error",
      })
    );
    return Promise.reject(new OfflineWriteBlockedError());
  }

  // Only a genuine Sanctum token goes on the wire. SPA requests authenticate through
  // the session cookie; `token` otherwise holds a non-secret session marker (see
  // utils/updateToken), and sending that as a Bearer credential achieved nothing.
  request.headers["Accept-Language"] = (i18n.language || "en").slice(0, 2);
  // The person's own calendar day: "visited today", "added 29 Oct" are worked out in this zone, not the server's UTC.
  const zone = browserTimeZone();
  if (zone) request.headers["X-Time-Zone"] = zone;
  const token = localStorage.getItem("token");
  if (isRealToken(token)) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  return request;
});

instance.interceptors.response.use(
  (response) => {
    // Deleting contacts is the way an over-limit account gets itself back
    // within its plan (both delete routes remain allowed by the backend even
    // while view-only restricted, Task 16). Refresh /me right after one so
    // the banner and refusal dialog lift the moment the account fits again,
    // rather than waiting on whatever next unrelated /me refresh happens to
    // come along. Cheap no-op the rest of the time: gated on the store
    // already being in the over-limit state.
    //
    // In practice every delete the Contacts page offers today (single row,
    // bulk-by-ids, bulk-exclude-ids) goes through the OTHER transport —
    // ApiService.call via SagaWrapper, not this `instance` — because
    // deleteHouseholdsByIds below is never called directly (browser-verified
    // 2026-09-15: a delete from the UI produced no /me refresh here). The
    // real fix for that path lives in lib/utils/createResponseDispatcher.js's
    // success branch. This block stays as the direct-call counterpart for
    // any caller that DOES use `instance` (e.g. a future admin tool, or code
    // like ContactIntakePanels/CreateHouseholdPage that calls householdsAPI
    // methods directly instead of dispatching a redux action) — same
    // reasoning as the error-side split already documented in
    // createResponseDispatcher.js and shouldShowGenericCreationError.ts.
    const method = (response.config?.method ?? "").toLowerCase();
    const url = response.config?.url ?? "";
    const deletedContacts =
      (method === "delete" && /\/api\/auth\/households\/\d+$/.test(url)) ||
      (method === "post" && url.endsWith("/api/auth/households/bulk-actions") && String(response.config?.data ?? "").includes('"delete"'));
    if (deletedContacts && store.getState()?.billingStore?.contactLimitOver) {
      store.dispatch({ type: "GET_USER" });
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("user");
      // Public v3 (I3): the sign-in page waits for this to know a saved "signed in" flag was stale.
      try { window.dispatchEvent(new Event("cy-session-ended")); } catch { /* no window events: the page's own timeout decides */ }
      store.dispatch(
        generalActions.errorAC({ message: "Unauthorized", type: "error" })
      );

      // Prevent redirect loops - don't redirect if already on /login. Without
      // this guard, landing unauthenticated on /login (which itself fires an
      // authenticated request that 401s) replaces the page with itself over
      // and over, reloading forever. Same guard shape as the 402 block below.
      const isAlreadyOnLoginPage = window.location.pathname === "/login";

      if (!isAlreadyOnLoginPage) {
        // Public v3 (P2.5, new look): sign-in goes back to the page that was open (in-app, allow-listed paths only).
        if (isPublicNext()) rememberWantedPage(window.location.pathname + window.location.search);
        window.location.replace("/login");
      }
    }

    // Handle mid-session rejection: a user who was auto-approved at registration and then
    // rejected by a super admin now fails ensure-user-is-approved. Log them out.
    // Walkthrough #5: a person still waiting for review (PENDING_APPROVAL) keeps the session and sees the "being reviewed"
    // page; signing him out sent him back to an empty sign-in form with no word.
    const pendingApproval = error.response?.status === 403 && error.response?.data?.code === "PENDING_APPROVAL";
    if (pendingApproval && window.location.pathname !== "/unAuthPage") {
      window.location.replace("/unAuthPage");
    }
    if (
      !pendingApproval &&
      error.response?.status === 403 &&
      typeof error.response?.data?.message === "string" &&
      error.response.data.message.toLowerCase().includes("not approved")
    ) {
      localStorage.removeItem("user");
      window.location.replace("/login");
    }

    // Stage 5A (final review M7): something that stays blocked while staff view as someone
    // (password, closing, payment ...) is refused by the server with this marker. Today's pages
    // have no place of their own for it, so say it in plain words here. The new layer's calls
    // (`nextLayer`) show it through their own toast instead.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "VIEWING_AS_BLOCKED" &&
      !(error.config as { nextLayer?: boolean } | undefined)?.nextLayer
    ) {
      store.dispatch(
        generalActions.errorAC({ message: i18n.t("next.access.errors.viewing_as_blocked"), type: "error" })
      );
    }

    // Handle 402 Payment Required
    if (error.response?.status === 402) {
      const { has_billing_permission, admin_contact, reason } = error.response.data || {};
      noteBillingRefusal(error.response.data?.message);
      const currentPath = window.location.pathname;

      // Store admin contact info for the AccountSuspendedPage
      if (admin_contact) {
        sessionStorage.setItem("adminContact", JSON.stringify(admin_contact));
      }

      // Prevent redirect loops - don't redirect if already on a billing/access gate page
      const isAlreadyOnAccessPage = (
        currentPath.startsWith("/billing") ||
        currentPath === "/pricing" ||
        // The Help page stays open while an account is locked (it shows Contact us only); other calls on it that answer 402 stay quiet.
        currentPath === "/help" ||
        currentPath === "/help/contact" ||
        // My settings stays open for the Owner of a locked account (walkthrough decision 7). The route guard sends everyone else on from there,
        // so the shell calls (project chip, bell count) that answer 402 on this page must not send the Owner back to Billing.
        currentPath === "/settings" ||
        currentPath === "/package-pending" ||
        currentPath === "/account-suspended"
      );

      if (!isAlreadyOnAccessPage) {
        if (reason === "not_yet_active") {
          window.location.replace("/package-pending");
        } else if (has_billing_permission) {
          // Users with billing permission go to pricing (trial expired) or billing (other)
          if (reason === "trial_expired") {
            window.location.replace("/pricing");
          } else {
            window.location.replace("/billing");
          }
        } else {
          // Users without billing permission always go to account-suspended
          window.location.replace(lockedLandingPath());
        }
      }
    }

    if (error.response?.status === 500 && !(error.config as { quietServerError?: boolean } | undefined)?.quietServerError) {
      store.dispatch(
        // Walkthrough public #28: plain words in the person's language (owner: no technical wording).
        generalActions.errorAC({ message: i18n.t("next.errors.server"), type: "error" })
      );
    }

    // Contact-limit refusal (Task 10): tell the app-wide upgrade dialog. Never
    // while inside the onboarding signup flow — ContactIntakePanels there
    // shows its own inline message instead, and popping this global dialog
    // over that flow would be a second, redundant notice (owner: registration
    // needs an inline message, the app needs a notification dialog — not both
    // at once, same split TrialWidget/CountryNudge already make for this flow).
    const limitInfo = contactLimitFrom(error);
    if (limitInfo && !window.location.pathname.startsWith(ONBOARDING_ROOT)) {
      window.dispatchEvent(new CustomEvent(CONTACT_LIMIT_EVENT, { detail: limitInfo }));
    }

    // View-only refusal (Task 16/17): the account already holds more contacts
    // than its plan covers, so the backend refused this write outright with
    // 403 OVER_CONTACT_LIMIT. Same never-during-onboarding guard as above —
    // ContactIntakePanels shows its own inline message there instead.
    const overLimit = overLimitFrom(error);
    if (overLimit && !window.location.pathname.startsWith(ONBOARDING_ROOT)) {
      window.dispatchEvent(new CustomEvent(OVER_LIMIT_EVENT, { detail: overLimit }));
    }

    return Promise.reject(error);
  }
);

export const testAPI = {
  testRequest() {
    return instance.get("/api/test");
  },
};

export const authAPI = {
  getToken() {
    return instance.get("/sanctum/csrf-cookie", {
      withCredentials: true,
      headers: {
        Accept: "application/json",
      },
    });
  },

  regUser(payload: RegUser) {
    return instance.post("/api/register", {
      first_name: payload.firstName,
      last_name: payload.lastName,
      email: payload.email,
      password: payload.password,
      mobile: payload.mobile,
      shlichus_country: payload.shlichus_country,
      shlichus_country_code: payload.shlichus_country_code,
      shlichus_country_iso: payload.shlichus_country_iso,
      shlichus_state: payload.shlichus_state,
      city: payload.city,
      city_lat: payload.city_lat,
      city_lng: payload.city_lng,
      city_place_id: payload.city_place_id,
      city_country_iso: payload.city_country_iso,
      affiliation: payload.affiliation,
      // Secret preview link: the code from the cookie, so the server lists the new person and account (nothing when there is no cookie).
      ...(publicPreviewCode() ? { preview_code: publicPreviewCode() } : {}),
    });
  },

  loginUser(payload: LoginUser) {
    return instance.post("/api/login", payload);
  },

  logoutUser() {
    return instance.post("/api/logout");
  },

  getUser(token: any) {
    return instance.get("/auth/user", token);
  },

  loginUserWithToken(payload: LoginWithToken) {
    return instance.post("/api/login-with-token", payload, {
      withCredentials: true,
    })
  },

  sendLoginLink(data: any) {
    return instance.post("/api/send-login-link", data);
  },

  /** Public v3 (P3.3): the first name a live sign-in link belongs to (null when the link is dead). */
  loginLinkOwner(token: string) {
    return instance.post("/api/login-link-owner", { token });
  },

  /** Walkthrough-public #18: is the e-mailed password-reset link still good? Answers { valid }. Looking does not spend it. */
  resetLinkCheck(data: { email: string; token: string }) {
    return instance.post("/api/reset-password-check", data);
  },

  stopImpersonation() {
    return instance.post("/api/auth/impersonate/stop");
  },

  getMe() {
    return instance.get("/api/auth/me");
  },
};

export const projectsAPI = {
  getProjects(status?: 'open' | 'closed' | 'all') {
    return instance.get('/api/auth/projects', { params: status ? { status } : undefined });
  },
  getProject(id: number) {
    return instance.get(`/api/auth/projects/${id}`);
  },
  createProject(data: { name: string; default_depot_address_id?: number | null }) {
    return instance.post('/api/auth/projects', data);
  },
  updateProject(id: number, data: { name?: string; color?: string; default_depot_address_id?: number | null }) {
    return instance.put(`/api/auth/projects/${id}`, data);
  },
  closeProject(id: number) {
    return instance.patch(`/api/auth/projects/${id}/close`);
  },
  openProject(id: number) {
    return instance.patch(`/api/auth/projects/${id}/open`);
  },
  deleteProject(id: number, confirmName?: string) {
    return instance.delete(`/api/auth/projects/${id}`, {
      data: confirmName ? { confirm_name: confirmName } : undefined,
    });
  },
  setCurrentProject(projectId: number | null) {
    return instance.put('/api/auth/me/current-project', { project_id: projectId });
  },
  setProjectFilter(state: Record<string, 'include' | 'exclude' | 'ignore'>) {
    return instance.put('/api/auth/me/project-filter', { state });
  },
  getProjectRoutes(projectId: number) {
    return instance.get(`/api/auth/projects/${projectId}/routes`);
  },
  bulkMoveRoutes(routeIds: number[], targetProjectId: number) {
    return instance.post('/api/auth/projects/bulk-move-routes', {
      route_ids: routeIds,
      target_project_id: targetProjectId,
    });
  },
};

export const workspaceAPI = {
  updateSettings(payload: Record<string, any>) {
    return instance.patch("/api/auth/workspace/settings", payload);
  },
};

export const supportAccessAPI = {
  updateSupportAccess(payload: { support_access_approved: boolean }) {
    return instance.put("/api/auth/support-access-approval", payload);
  },
};

export const passwordAPI = {
  updatePassword(payload: {
    password: string;
    password_confirmation: string;
    current_password?: string;
  }) {
    return instance.put("/api/auth/password", payload);
  },
  markPromptShown() {
    return instance.post("/api/auth/password-prompt-shown");
  },
};

export const addressAPI = {
  getTypes() {
    return instance.get("api/auth/addresses/get-available-types");
  },
  getAddress() {
    return instance.get("api/auth/addresses");
  },
  refreshPlaceIds() {
    return instance.post("api/auth/addresses/refresh-place-ids");
  },
};

export const categoriesAPI = {
  getCategories() {
    return instance.get("api/auth/categories");
  },
};

// export const householdsAPI = {
// (payload: object) {
//     return instance.get(
//       `api/auth/households?with[]=categories&with[]=visits&with[]=scheduledVisits&with[]=mainPerson&with[]=address`,
//       {
//         params: payload,
//       }
//     );
//   },
export const householdsAPI = {
  // Direct call (not routed through the CREATE_HOUSEHOLD redux-saga action) for
  // callers that can't rely on a Redux <Provider> being mounted above them —
  // e.g. OnboardingPreview's ShliachBuildList, which adds contacts inline
  // during registration. Same endpoint the saga's createHousehold hits.
  createHousehold(data: object) {
    return instance.post("/api/auth/households", data);
  },

  getHouseholds(payload: object) {
    return instance.get(
      `api/auth/households-for-list?with[]=categories&with[]=visits&with[]=scheduledVisits&with[]=mainPerson&with[]=address`,
      {
        params: payload,
      }
    );
  },

  /**
   * Households WITH their address, for pickers that need coordinates.
   *
   * NOT `households-for-list`: despite accepting `with[]=address`, that endpoint's
   * resource returns only id / periodicity / householdName / categories /
   * scheduledVisitExists. Both `address` and `address_id` are dropped, so every
   * caller that asks "can this be a map pin or a route stop?" gets `undefined`
   * and silently concludes NO for every household — see
   * utils/mappableHousehold.ts, which reads `household.address`.
   *
   * Deliberately leaner than getHouseholdDetails (no persons/phones/addresses):
   * a picker needs a name, an id and a coordinate pair, not the whole record.
   */
  getHouseholdsWithAddress(payload: object) {
    return instance.get("api/auth/households?with[]=address&with[]=mainPerson", {
      params: payload,
    });
  },

  // Full household detail in bulk, with the SAME relation expansion getHouseholdsById
  // asks for one at a time. Used only by the offline prefetch: the contact card, the
  // edit form and the report wizard all read a per-household URL that nothing warms,
  // and fetching those one by one would be thousands of requests. Paged, so a large
  // workspace arrives in a handful of responses instead of one enormous one.
  getHouseholdDetails(payload: object) {
    return instance.get(
      `api/auth/households?with[]=address&with[]=homeAddress&with[]=housePhone&with[]=categories&with[]=persons&with[]=persons.phones&with[]=persons.addresses`,
      { params: payload }
    );
  },

  getHouseholdsDownload(payload: object) {
    return instance.get(
      `api/auth/households-for-download?with[]=categories&with[]=visits&with[]=scheduledVisits&with[]=mainPerson&with[]=address`,
      {
        params: payload,
      }
    );
  },

  // Canonical per-contact CSV for the currently filtered list (one row per contact,
  // import-compatible columns incl. Work Ext + work address). Returns a CSV blob.
  downloadFilteredCsv(payload: object) {
    return instance.get(`api/auth/households/export-filtered-as-download-file`, {
      params: payload,
      responseType: "blob",
    });
  },

  getHouseholdCategories() {
    return instance.get(`api/auth/categories`);
  },

  createRegularCategory(data: string) {
    return instance.post(`/api/auth/categories`, { name: data });
  },

  deleteRegularCategory(id: number) {
    return instance.delete(`/api/auth/categories/${id}?force=1`);
  },

  updateRegularCategory(id: number, name: string) {
    return instance.put(`/api/auth/categories/${id}`, { name, refresh: 1 });
  },

  deleteHouseholdsByIds(ids: number[]) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "delete",
      ids,
    });
  },

  deleteAllHouseholdsExcludeIds(excludedIds: number[]) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "delete",
      all: true,
      excluded_ids: excludedIds,
    });
  },

  updateRegularCategoriesForHouseholdsByIds(
    ids: number[],
    category_ids: number[]
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "update_regular_categories",
      ids,
      category_ids,
    });
  },

  // Additive attach — syncWithoutDetaching on the backend (HouseholdService::
  // bulkAddRegularCategories), unlike updateRegularCategoriesForHouseholdsByIds
  // above, which REPLACES each household's whole category set. Added for
  // OnboardingPreview's inline "Create a contact list" (ShliachListOrRoute.tsx):
  // attaching a brand-new category must not strip categories a household
  // already carries. The backend action already existed; this is the first
  // frontend caller of it.
  addRegularCategoriesForHouseholdsByIds(
    ids: number[],
    category_ids: number[]
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "add_regular_categories",
      ids,
      category_ids,
    });
  },

  /**
   * Takes categories OFF households — `detach` on the backend
   * (HouseholdService::bulkRemoveRegularCategories).
   *
   * Deliberately NOT expressed as "update with a shorter list": that is
   * `update_regular_categories`, whose `sync()` replaces each household's whole
   * set and silently strips every category the caller did not name. Detaching
   * removes only what is asked for, and a household that never carried the
   * category is simply unaffected — so a mixed selection (some with it, some
   * with it alongside others, some without it at all) comes out right with no
   * special cases and no need to work out first who holds what.
   */
  removeRegularCategoriesForHouseholdsByIds(
    ids: number[],
    category_ids: number[]
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "remove_regular_categories",
      ids,
      category_ids,
    });
  },

  /** The detach above, applied to every household matching the list's filters. */
  removeRegularCategoriesForAllHouseholdsExcludeIds(
    excludedIds: number[],
    category_ids: number[],
    filters?: object
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "remove_regular_categories",
      all: true,
      excluded_ids: excludedIds,
      category_ids,
      filters,
    });
  },

  updateRegularCategoriesForAllHouseholdsExcludeIds(
    excludedIds: number[],
    category_ids: number[]
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "update_regular_categories",
      all: true,
      excluded_ids: excludedIds,
      category_ids,
    });
  },

  updateSchedulingForHouseholdsByIds(ids: number[], periodicity: string) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "update_periodicity",
      ids,
      periodicity,
    });
  },

  updateSchedulingForAllHouseholdsExcludeIds(
    excludedIds: number[],
    periodicity: string
  ) {
    return instance.post(`/api/auth/households/bulk-actions`, {
      action: "update_periodicity",
      all: true,
      excluded_ids: excludedIds,
      periodicity,
    });
  },

  getHouseholdsById(id: number) {
    return instance.get(
      `api/auth/households/${id}?with[]=address&with[]=homeAddress&with[]=housePhone&with[]=categories&with[]=persons&with[]=persons.phones&with[]=persons.addresses`
    );
  },

  getPeriodicity() {
    return instance.get("api/auth/households/get-available-periodicity");
  },

  getHouseholdNames() {
    return instance.get("api/auth/households-names");
  },

  importPreview(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return instance.post("/api/auth/households/import/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  importDryRun(file: File, mapping?: Record<string, number>) {
    const formData = new FormData();
    formData.append("file", file);
    if (mapping && Object.keys(mapping).length > 0) {
      formData.append("mapping", JSON.stringify(mapping));
    }
    return instance.post("/api/auth/households/import/dry-run", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  importHouseholds(file: File, mapping?: Record<string, number>, skipInvalid?: boolean) {
    const formData = new FormData();
    formData.append("file", file);
    if (mapping && Object.keys(mapping).length > 0) {
      formData.append("mapping", JSON.stringify(mapping));
    }
    if (skipInvalid) {
      formData.append("skip_invalid", "1");
    }
    return instance.post("/api/auth/households/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const personAPI = {
  getSalutations() {
    return instance.get("api/auth/persons/get-available-salutations");
  },
};

export const visitReportsAPI = {

  getAllVisits(payload: object) {
    return instance.get(
      `api/auth/visits-for-list`,
      {
        params: payload,
      }
    );
  },

  getVisits(id: number, page: number, perPage = 5, scheduled?: number) {
    return instance.get(`api/auth/visits`, {
      params: {
        household_id: id,
        scheduled,
        page,
        perPage,
      },
    });
  },

  getNextHouseholdVisit(id: number) {
    return instance.get(`api/auth/households/${id}/get-next-scheduled-visit`);
  },

  getStatuses() {
    return instance.get("api/auth/visits/get-available-statuses");
  },

  getReactions() {
    return instance.get("api/auth/visits/get-available-reactions");
  },
  getAddressError() {
    return instance.get("api/auth/visits/get-available-address-error-options");
  },

  createNextVisit(id: number, data: any) {
    return instance.put(`api/auth/visits/${id}`, data);
  },
};

export const routesAPI = {
  getRoutes({
    perPage = 10,
    page = 1,
    household,
    address,
    searchValue,
  }: GetRoutes) {
    return instance.get(`api/auth/routes`, {
      params: {
        name: searchValue || undefined,
        perPage,
        page,
        with: [
          household && "points.household",
          address && "points.address",
          "startAddress",
        ],
      },
    });
  },

  getRoute({ id, household, address, points }: GetRoute) {
    return instance.get(`api/auth/routes/${id}`, {
      params: {
        with: [
          household && "points.household",
          address && "points.address",
          points && "points",
        ],
      },
    });
  },

  createRoute(data: CreateRoute) {
    return instance.post("api/auth/routes", data);
  },

  updateRoute({ id, data }: UpdateRoute) {
    return instance.put(`api/auth/routes/${id}`, data);
  },

  // The optional idempotencyKey is only ever passed by the offline sync engine when
  // it replays a queued tap. Live calls send no header and behave exactly as before.
  markPointVisited(routeId: number, pointId: number, idempotencyKey?: string) {
    return instance.patch(
      `api/auth/routes/${routeId}/points/${pointId}/visited`,
      undefined,
      idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : undefined
    );
  },

  unmarkPointVisited(routeId: number, pointId: number, idempotencyKey?: string) {
    return instance.delete(
      `api/auth/routes/${routeId}/points/${pointId}/visited`,
      idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : undefined
    );
  },

  deleteRoute(id: number) {
    return instance.delete(`api/auth/routes/${id}`);
  },

  bulkDeleteRoutes(ids: number[]) {
    return instance.post(`api/auth/routes/bulk-actions`, {
      action: "delete",
      ids,
    });
  },

  deleteAllRoutes() {
    return instance.post(`api/auth/routes/bulk-actions`, {
      action: "delete",
      all: true,
      excluded_ids: [],
    });
  },
};

export const routeDivisionAPI = {
  divideRoutes(data: any) {
    return instance.post("api/auth/routes/divide", data);
  },
  divideBySelection(data: any) {
    return instance.post("api/auth/routes/divide-by-selection", data);
  },
  getCities(state: string) {
    return instance.get("api/auth/routes/division/cities", { params: { state } });
  },
  // Country-only division (e.g. Israel): the geocoded household IDs in a country,
  // optionally narrowed to a Google-picked city. Fed into the selection flow.
  getDivisionHouseholdIds(country: string, city?: string) {
    return instance.get("api/auth/routes/division/household-ids", {
      params: { country, ...(city ? { city } : {}) },
    });
  },
  preflightRouteDivision(data: any) {
    return instance.post("api/auth/routes/divide/preflight", data);
  },
  optimizeRoute(routeId: number) {
    return instance.post(`api/auth/routes/${routeId}/optimize`);
  },
};

export const pointsAPI = {
  deletePoint(id: number) {
    return instance.delete(`api/auth/points/${id}`);
  },
};

export const teamAPI = {
  getTeam({ perPage = 10, page = 1, searchValue = null }: GetTeam) {
    return instance.get(`api/auth/invitations`, {
      params: {
        perPage,
        page,
        full_name: searchValue,
      },
    });
  },

  deleteTeam(id: string) {
    return instance.delete(`api/auth/invitations/${id}`);
  },

  resendLoginLink(id: string) {
    return instance.post(`/api/auth/invitations/${id}/resend-link`);
  },
};

export const notificationsAPI = {
  getNotifications({ perPage = 10, page = 1, typeSearch }: GetNotifications) {
    return instance.get(`/api/auth/notifications`, {
      params: {
        perPage,
        page,
        type: typeSearch,
      },
    });
  },
  getNotification(id: string) {
    return instance.get(`api/auth/notifications/${id}`);
  },
  getUnreadNotificationsCount() {
    return instance.get("/api/auth/notifications/count-unread-notifications");
  },
  markNotificationAsRead(id: string) {
    return instance.post(`/api/auth/notifications/${id}/mark-as-read`);
  },
};

export const dashboardAPI = {
  getDashboardStats() {
    return instance.get("/api/auth/dashboard/stats");
  },
};

export const billingAPI = {
  getPlans() {
    return instance.get("/api/auth/billing/plans");
  },

  getSubscription() {
    return instance.get("/api/auth/billing/subscription");
  },

  createCheckoutSession(data: {
    plan_id: number;
    billing_cycle: "monthly" | "yearly" | "onetime";
    success_url: string;
    cancel_url: string;
  }) {
    return instance.post("/api/auth/billing/checkout-session", data);
  },

  createPortalSession(returnUrl?: string) {
    return instance.post("/api/auth/billing/portal", { return_url: returnUrl });
  },

  getInvoices(params?: { perPage?: number; page?: number }) {
    return instance.get("/api/auth/billing/invoices", { params });
  },

  // POST (not DELETE) because it now carries a body that some clients drop on DELETE.
  // `after_end` defaults to "pause" server-side; "close" also needs `confirm_name`.
  cancelSubscription(data?: { after_end?: "pause" | "close"; confirm_name?: string }) {
    return instance.post("/api/auth/billing/cancel", data ?? {});
  },

  // Close an account that has no plan to cancel (paused / view-only).
  closeAccount(confirmName: string) {
    return instance.post("/api/auth/billing/close-account", { confirm_name: confirmName });
  },

  // Withdraw a pending closure; the account becomes paused (view-only, data kept).
  keepAccount() {
    return instance.post("/api/auth/billing/keep-account");
  },

  resumeSubscription() {
    return instance.post("/api/auth/billing/resume");
  },

  // Consented cancel of the account's own recurring monthly donation (distinct from
  // cancelSubscription above, which refuses donation/free-sourced rows with 422
  // SUBSCRIPTION_NOT_PURCHASED). `confirm: true` is required by the backend so a stray
  // retry can never cancel a gift by accident.
  cancelDonation() {
    return instance.post("/api/auth/billing/donation/cancel", { confirm: true });
  },

  // Payment Methods
  getPaymentMethods() {
    return instance.get("/api/auth/billing/payment-methods");
  },

  addPaymentMethod(paymentMethodId: string) {
    return instance.post("/api/auth/billing/payment-methods", { payment_method_id: paymentMethodId });
  },

  deletePaymentMethod(id: string) {
    return instance.delete(`/api/auth/billing/payment-methods/${id}`);
  },

  setDefaultPaymentMethod(id: string) {
    return instance.put(`/api/auth/billing/payment-methods/${id}/default`);
  },

  getSetupIntent() {
    return instance.post("/api/auth/billing/setup-intent");
  },

  // Payment History
  getPaymentHistory(params?: { perPage?: number; page?: number }) {
    return instance.get("/api/auth/billing/history", { params });
  },

  // Subscribe/Purchase
  subscribe(data: { package_id: number; payment_method_id?: string }) {
    return instance.post("/api/auth/billing/subscribe", data);
  },

  purchase(data: { package_id: number; payment_method_id?: string }) {
    return instance.post("/api/auth/billing/purchase", data);
  },

  changePlan(data: { package_id: number }) {
    return instance.put("/api/auth/billing/change-plan", data);
  },

  // Customer Info
  getCustomer() {
    return instance.get("/api/auth/billing/customer");
  },

  updateCustomer(data: { name: string; email: string }) {
    return instance.put("/api/auth/billing/customer", data);
  },

  // Invoice Details
  getInvoice(invoiceId: string) {
    return instance.get(`/api/auth/billing/invoices/${invoiceId}`);
  },

  // Onboarding / managed upload
  dismissWelcome() {
    return instance.put("/api/auth/billing/dismiss-welcome");
  },

  purchaseSetupFee(planId: number) {
    return instance.post("/api/auth/billing/purchase-setup-fee", { plan_id: planId });
  },

  uploadManagedFile(formData: FormData) {
    return instance.post("/api/auth/billing/upload-managed-file", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  getManagedUploadFiles() {
    return instance.get("/api/auth/billing/managed-upload-files");
  },

  getUsage() {
    return instance.get("/api/auth/billing/usage");
  },

  getPricingSummary() {
    return instance.get("/api/auth/billing/pricing-summary");
  },

  contactSales(data: { contact_info: { name?: string; email: string; phone?: string }; message?: string; attempted_plan: string }) {
    return instance.post("/api/auth/billing/contact-sales", data);
  },

  confirmSetupPurchase() {
    return instance.get("/api/auth/billing/confirm-setup-purchase");
  },

  downloadImportTemplate() {
    return instance.get("/api/auth/import/template", { responseType: "blob" });
  },

  downloadImportExample() {
    return instance.get("/api/auth/import/example", { responseType: "blob" });
  },
};

// Public (unauthenticated) API — usable from the marketing landing pages,
// which have no logged-in user or workspace context.
export const publicAPI = {
  contactUs(data: { name: string; email: string; phone?: string; message?: string }) {
    return instance.post("/api/contact-us", data);
  },

  // What the public Donate page may offer (the "cover the card fee" box shows only while staff have it on).
  getDonationOptions() {
    return instance.get<{ cover_fee: boolean }>("/api/donate/options");
  },

  createDonationCheckoutSession(data: {
    amount: number;
    cover_fee?: boolean;
    frequency: "one_time" | "monthly";
    donor_name?: string;
    donor_email?: string;
    message?: string;
  }) {
    return instance.post("/api/donate/checkout-session", data);
  },

  // Reconciles a Checkout Session's payment status directly with Stripe —
  // called after Stripe Embedded Checkout's client-side `onComplete` fires,
  // since that callback alone is not proof the payment (or our webhook)
  // actually succeeded. Returns { status: 'succeeded' | 'pending' | ... }.
  getDonationCheckoutSessionStatus(sessionId: string) {
    return instance.get(`/api/donate/checkout-session/${encodeURIComponent(sessionId)}/status`);
  },
};

// Admin API
export const adminAPI = {
  // Workspaces
  getWorkspaces(params?: { perPage?: number; page?: number; search?: string }) {
    return instance.get("/api/admin/workspaces", { params });
  },

  grantPackage(data: { workspace_id: number; plan_id?: number; start_date: string; expires_at: string }) {
    return instance.post(`/api/admin/workspaces/${data.workspace_id}/grant-package`, {
      plan_id: data.plan_id,
      start_date: data.start_date,
      expires_at: data.expires_at,
    });
  },

  updateWorkspacePackage(data: { workspace_id: number; start_date: string; expires_at: string }) {
    return instance.put(`/api/admin/workspaces/${data.workspace_id}/update-package`, {
      start_date: data.start_date,
      expires_at: data.expires_at,
    });
  },

  revokePackage(workspaceId: number) {
    return instance.post(`/api/admin/workspaces/${workspaceId}/revoke-access`, {});
  },

  startWorkspaceTrial(workspaceId: number, days?: number) {
    return instance.post(`/api/admin/workspaces/${workspaceId}/start-trial`, days ? { days } : {});
  },

  stopWorkspaceTrial(workspaceId: number) {
    return instance.post(`/api/admin/workspaces/${workspaceId}/stop-trial`, {});
  },

  // `unlimited: true` forces the workspace to have NO contact limit at all, even
  // while it holds an active paid subscription/purchase whose plan has its own
  // limit. This used to be represented by sending `contact_limit: null`, but the
  // backend (rightly) reads null as "no override set" and falls back to the
  // active plan's own limit — so "granting unlimited access" silently did
  // nothing whenever a package was active. `unlimited` is a dedicated flag so
  // it can never collide with "no override".
  updateWorkspaceContactLimit(workspaceId: number, contactLimit: number | null, unlimited = false) {
    return instance.put(`/api/admin/workspaces/${workspaceId}/contact-limit`, {
      contact_limit: contactLimit,
      unlimited,
    });
  },

  // Super-admin-only: the per-workspace location (country + city) that drives
  // country-aware geocoding. Regular users cannot change these.
  updateWorkspaceLocation(
    workspaceId: number,
    data: { country_code?: string | null; country_name?: string | null; city?: string | null; city_country_iso?: string | null; shlichus_state?: string | null }
  ) {
    return instance.put(`/api/admin/workspaces/${workspaceId}/location`, data);
  },

  // Donation visibility + the consented "stop the donation" path, super-admin only
  // (owner decision: staff can stop a donor's monthly gift on request — e.g. a phone
  // call — even though a donor cancelling in Stripe already syncs back automatically).
  getWorkspaceDonation(workspaceId: number) {
    return instance.get(`/api/admin/workspaces/${workspaceId}/donation`);
  },

  cancelWorkspaceDonation(workspaceId: number) {
    return instance.post(`/api/admin/workspaces/${workspaceId}/donation/cancel`, { confirm: true });
  },

  // Users
  getUsers(params?: { perPage?: number; page?: number; search?: string }) {
    return instance.get("/api/admin/users", { params });
  },

  updateUser(data: { id: number; first_name?: string; last_name?: string; email?: string }) {
    return instance.put(`/api/admin/users/${data.id}`, data);
  },

  updateUserWorkspaceRole(data: { id: number; workspace_id: number; role: string }) {
    return instance.put(`/api/admin/users/${data.id}/workspaces/${data.workspace_id}/role`, { role: data.role });
  },

  /**
   * What deleting this user would actually do. Read BEFORE deleting: a user who
   * administers a workspace cannot be deleted by assertion any more, because doing so
   * used to leave the workspace live, fully populated, and impossible for anyone to
   * manage. See UserDeletionPlanner on the backend.
   */
  getUserDeletionImpact(id: number) {
    return instance.get<UserDeletionImpact>(`/api/admin/users/${id}/deletion-impact`);
  },

  /**
   * `workspaces` is required for every workspace this user administers — the request is
   * refused with 422 (and the impact attached) otherwise.
   */
  deleteUser(id: number, payload?: { workspaces?: Record<number, UserDeletionDecision> }) {
    return instance.delete(`/api/admin/users/${id}`, { data: payload ?? {} });
  },

  /** What restoring this deleted user would do, and what stands in the way. */
  getUserRestoreImpact(id: number) {
    return instance.get<UserRestoreImpact>(`/api/admin/users/${id}/restore-impact`);
  },

  restoreUser(id: number) {
    return instance.post(`/api/admin/users/${id}/restore`);
  },

  /** Walkthrough M9: the logins deleted in the last 90 days (owner decision 10), for Restore. */
  getDeletedUsers(search?: string) {
    return instance.get<{ data: DeletedLogin[] }>("/api/admin/users/deleted", { params: search ? { search } : undefined });
  },

  /**
   * Close an account and everything in it. Soft and reversible, but it takes thousands
   * of households on one click, so the workspace's own name must be typed back.
   */
  /** What closing this account would take with it, including who it would strand. */
  getWorkspaceDeletionImpact(id: number) {
    return instance.get(`/api/admin/workspaces/${id}/deletion-impact`);
  },

  /**
   * Console delete asks each time (owner, 2026-10-13): "close" = the customer's 90-day
   * closure (access ends now, data deleted after 90 days, reopenable until then);
   * "permanent" = delete everything now, irreversibly.
   */
  deleteWorkspace(id: number, confirmName: string, mode: "close" | "permanent" = "close") {
    return instance.delete(`/api/admin/workspaces/${id}`, {
      data: { confirm_name: confirmName, mode },
    });
  },

  /** Preview mode: turn the new design on / off for one account (full Super Admin only; audited by the server). */
  setWorkspaceNewDesign(id: number, on: boolean) {
    return on
      ? instance.post("/api/auth/next/staff/preview/accounts", { workspace_id: id })
      : instance.delete(`/api/auth/next/staff/preview/accounts/${id}`);
  },

  /** Cancel a pending closure (before its deletion date). */
  reopenWorkspace(id: number) {
    return instance.post(`/api/admin/workspaces/${id}/reopen`);
  },

  getWorkspaceRestoreImpact(id: number) {
    return instance.get(`/api/admin/workspaces/${id}/restore-impact`);
  },

  restoreWorkspace(id: number) {
    return instance.post(`/api/admin/workspaces/${id}/restore`);
  },

  // Packages
  getPackages(params?: { perPage?: number; page?: number }) {
    return instance.get("/api/admin/plans", { params });
  },

  createPackage(data: any) {
    return instance.post("/api/admin/plans", data);
  },

  updatePackage(data: { id: number } & Record<string, any>) {
    const { id, ...rest } = data;
    return instance.put(`/api/admin/plans/${id}`, rest);
  },

  deletePackage(id: number) {
    return instance.delete(`/api/admin/plans/${id}`);
  },

  // Managed Uploads
  getManagedUploads() {
    return instance.get("/api/admin/managed-uploads");
  },

  downloadManagedFile(workspaceId: number, filename?: string) {
    return instance.get(
      `/api/admin/managed-uploads/${workspaceId}/download${filename ? "/" + encodeURIComponent(filename) : ""}`,
      { responseType: "blob" }
    );
  },

  updateManagedUploadStatus(workspaceId: number, status: string) {
    return instance.put(`/api/admin/managed-uploads/${workspaceId}/status`, { status });
  },

  requestManagedReupload(workspaceId: number, message: string) {
    return instance.post(`/api/admin/managed-uploads/${workspaceId}/request-reupload`, { message });
  },

  getManagedUploadFiles(workspaceId: number) {
    return instance.get(`/api/admin/managed-uploads/${workspaceId}/files`);
  },

  // Impersonation ("View as"). Start answers {data: {key, expires_in}}: a one-time key, not a
  // token (stage 5A B7). The key is traded once for the viewing-as token by exchangeViewAsKey.
  // When the person's Support access switch is off, staff must give an emergency reason (10+ characters).
  impersonate(userId: number, workspaceId?: number, emergencyReason?: string) {
    return instance.post(`/api/admin/users/${userId}/impersonate`, {
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
      ...(emergencyReason ? { emergency_reason: emergencyReason } : {}),
    });
  },

  exchangeViewAsKey(key: string) {
    return instance.post("/api/auth-spa/view-as/exchange", { key });
  },

  // Pending user approval
  getPendingUsers() {
    return instance.get("/api/auth/admin/pending-users");
  },

  approveUser(userId: number, role: 'admin' | 'anash' = 'admin') {
    return instance.post(`/api/auth/admin/users/${userId}/approve`, { role });
  },

  rejectUser(userId: number) {
    return instance.post(`/api/auth/admin/users/${userId}/reject`);
  },

  // Dashboard
  getDashboardStats() {
    return instance.get("/api/admin/dashboard/stats");
  },

  refreshDashboardStats() {
    return instance.post("/api/admin/dashboard/refresh");
  },

  // Engine settings
  getEngineSettings() {
    return instance.get("/api/admin/settings/engine");
  },

  updateEngineSettings(data: {
    routing_engine?: string;
    geocoding_engine?: string;
    routing_google_fallback_enabled?: boolean;
    routing_unsupported_countries_fallback_enabled?: boolean;
    photon_enforce_housenumber?: boolean;
    geocoding_google_fallback_enabled?: boolean;
    geocoding_shadow_enabled?: boolean;
    oa_enabled?: boolean;
    geocoding_force_israel?: boolean;
  }) {
    return instance.patch("/api/admin/settings/engine", data);
  },

  getOaHealth() {
    return instance.get("/api/admin/settings/oa-health");
  },

  /**
   * Geocode ONE workspace now, ignoring the backoff schedule, optionally forcing the
   * Google fallback for that run only. Answers 202 immediately — the run itself is
   * queued, because a workspace can hold tens of thousands of addresses.
   */
  runGeocodingForWorkspace(data: {
    workspace_id: number;
    force_google?: boolean;
    /** Forget what our engines previously answered for this workspace and ask again —
     *  the recovery path for an engine that was up but answering wrongly. Free. */
    retry_given_up?: boolean;
  }) {
    return instance.post("/api/admin/geocoding/run", data);
  },

  getGeocodingShadowSummary(params?: { page?: number; perPage?: number; category?: string }) {
    return instance.get("/api/admin/geocoding-shadow/summary", { params });
  },

  getSuspectAddresses(params?: {
    page?: number;
    perPage?: number;
    threshold?: number;
    search?: string;
    sort?: "distance_desc" | "distance_asc" | "checked_at_desc" | "state";
  }) {
    return instance.get("/api/admin/routing/suspect-addresses", { params });
  },

  // Chabad Centres
  /**
   * `status` omitted = every centre, whatever state it is in — that is what the
   * All centres tab asks for. `trashed: "only"` lists archived centres,
   * `"with"` mixes them in; omitted keeps them out.
   */
  getCenters(params?: {
    status?: string;
    search?: string;
    trashed?: "with" | "only";
    page?: number;
    perPage?: number;
  }) {
    return instance.get("/api/admin/centers", { params });
  },

  /** One centre with everyone attached to it — what View opens. Includes archived. */
  getCenter(centerId: number) {
    return instance.get(`/api/admin/centers/${centerId}`);
  },

  /**
   * A centre entered by hand. Approved on creation: the approval queue exists to
   * ask a super admin whether a centre is real, and this one came from one.
   * Requires a full Google pick, same as the address repair.
   */
  createCenter(center: {
    name: string;
    phone?: string;
    formatted_address: string;
    street_name?: string;
    city: string;
    state: string;
    zip?: string;
    country: string;
    country_iso: string;
    lat?: number;
    lng?: number;
    place_id: string;
  }) {
    return instance.post("/api/admin/centers", center);
  },

  /** Name and phone only — the address has its own path, which fans the change out. */
  updateCenter(centerId: number, fields: { name?: string; phone?: string | null }) {
    return instance.patch(`/api/admin/centers/${centerId}`, fields);
  },

  /** Archive: gone from the directory and from search, kept for the people attached. */
  deleteCenter(centerId: number) {
    return instance.delete(`/api/admin/centers/${centerId}`);
  },

  restoreCenter(centerId: number) {
    return instance.post(`/api/admin/centers/${centerId}/restore`, {});
  },

  /**
   * Shliachim waiting to be confirmed at a centre. Defaults to pending; volunteers
   * are never here (those links are created approved).
   */
  getCenterConnections(params?: { status?: string; page?: number; perPage?: number }) {
    return instance.get("/api/admin/centers/connections", { params });
  },

  approveCenterConnection(connectionId: number) {
    return instance.post(`/api/admin/center-connections/${connectionId}/approve`, {});
  },

  rejectCenterConnection(connectionId: number) {
    return instance.post(`/api/admin/center-connections/${connectionId}/reject`, {});
  },

  /**
   * Every centre whose address was typed rather than picked, across EVERY
   * status. Deliberately not a filter on the queue above: approving does not
   * clear the flag, so a pending-only view would drop exactly the centres this
   * list exists to surface.
   */
  getFlaggedCenters(params?: { page?: number; perPage?: number }) {
    return instance.get("/api/admin/centers/flagged", { params });
  },

  approveCenter(centerId: number) {
    return instance.post(`/api/admin/centers/${centerId}/approve`, {});
  },

  rejectCenter(centerId: number, reason?: string) {
    return instance.post(`/api/admin/centers/${centerId}/reject`, { reason });
  },

  /**
   * The only thing that clears `address_verified`. Requires a full Google pick
   * (lat/lng/place_id/city/state/country); the server re-runs the workspace
   * address seeding for that centre's Shliach on success.
   */
  updateCenterAddress(
    centerId: number,
    address: {
      formatted_address: string;
      street_name?: string;
      city: string;
      state: string;
      zip?: string;
      country: string;
      country_iso: string;
      lat?: number;
      lng?: number;
      place_id: string;
    }
  ) {
    return instance.patch(`/api/admin/centers/${centerId}/address`, address);
  },

  /**
   * A Shliach-proposed correction to their centre's address, pending review.
   * Not visible to anyone else until a super admin approves it here.
   */
  getAddressProposals(params?: { page?: number; perPage?: number }) {
    return instance.get("/api/admin/centers/address-proposals", { params });
  },

  approveAddressProposal(proposalId: number) {
    return instance.post(`/api/admin/centers/address-proposals/${proposalId}/approve`, {});
  },

  rejectAddressProposal(proposalId: number, reason?: string) {
    return instance.post(`/api/admin/centers/address-proposals/${proposalId}/reject`, { reason });
  },
};

// Bridge for the new layer (src/next/bridges/apiClient.ts): same instance, same interceptors.
export { instance as legacyAxios };
